import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Aviso,
  Botao,
  Campo,
  Cartao,
  CartaoIndicador,
  CelulaDeAcoes,
  EstadoVazio,
  IconeLixeira,
  Selo,
  Tabela,
  ValorFiscal,
  ValorPercentual,
  formatarMoeda,
  useDialogo,
  type ColunaTabela,
} from '../../componentes/ui';
import { AvisoDoLote, BotaoDoLote, useAcaoEmLote } from '../../componentes/AcaoEmLote';
import { BotaoExtrairXlsx } from '../../componentes/BotaoExtrairXlsx';
import { PedidosDaOperacao, invalidarPedidos } from '../../componentes/PedidosDeFechamento';
import { atenderPedido, type PedidoDeFechamento } from '../../servicos/participacao';
import { requisicaoAutenticada } from '../../servicos/http';
import { resumirNomes } from '../../servicos/lote';
import { diaLocal, type ColunaDaPlanilha } from '../../servicos/planilha';
import { ErroDaApi } from '../ErroDaApi';
import { Pagina } from '../Carteira';
import { ModalDeNegociacao, type InicioDaNegociacao } from './ModalDeNegociacao';
import { DetalheDaNegociacao } from './DetalheDaNegociacao';
import { FiltroDoPontual } from './FiltroDoPontual';
import { ParcelasAReceber } from './ParcelasAReceber';
import { QUEM, ROTULO_DO_STATUS, ROTULO_DO_TIPO, TOM_DO_STATUS } from './comum';
import {
  FILTRO_DO_PONTUAL_VAZIO,
  naBusca,
  negociacoesNoFiltro,
  type EscolhasDoPontual,
} from './filtro';
import type { Negociacao } from './tipos';

/**
 * ICMS Pontual — a mesa de negociação.
 *
 * ## O que esta tela é
 *
 * A lista das compras e vendas de crédito: quem vende, quem compra, quem
 * intermedeia, por quanto, e em que ponto do ciclo cada uma está. É a tela que
 * responde "o que está para transferir" e "o que está para receber" — as duas
 * perguntas que a operação faz num negócio que não tem mês nem apuração.
 *
 * ## Por que não parece a do Recorrente
 *
 * Porque a operação é outra: não há competência, SPED nem cronograma de
 * e-mails. O ciclo inteiro cabe em quatro estados, e a tela mostra os quatro na
 * mesma linha em vez de esconder o andamento atrás de abas.
 */

/**
 * As negociações com as duas pontas e os valores — inclusive spread e deságio
 * de compra e venda: a planilha é da operação, não vai para cliente.
 */
const COLUNAS_DA_PLANILHA: readonly ColunaDaPlanilha<Negociacao>[] = [
  { titulo: 'Fornecedor', largura: 36, valor: (n) => n.fornecedorNome },
  { titulo: 'Comprador', largura: 36, valor: (n) => n.compradorNome },
  { titulo: 'Assessor', largura: 30, valor: (n) => n.parceiroNome },
  {
    titulo: 'Tipo de crédito',
    largura: 14,
    valor: (n) => ROTULO_DO_TIPO[n.tipoCredito] ?? n.tipoCredito,
  },
  { titulo: 'Situação', largura: 16, valor: (n) => ROTULO_DO_STATUS[n.status] ?? n.status },
  {
    titulo: 'Próxima ação',
    largura: 36,
    valor: (n) =>
      n.proximaAcao.responsavel === null
        ? null
        : `${n.proximaAcao.resumo} · ${QUEM[n.proximaAcao.responsavel] ?? ''}`,
  },
  { titulo: 'Protocolo', largura: 18, valor: (n) => n.protocoloTransferencia },
  // Os nomes da tabela (14/10/2026): cada deságio pelo lado de quem o dá — o
  // fornecedor vende, o comprador compra.
  { titulo: '$ Negociação', formato: 'moeda', valor: (n) => n.valorFace },
  { titulo: '% Deságio venda', formato: 'percentual', valor: (n) => n.desagioCompraPercentual },
  { titulo: 'Fornecedor recebe', formato: 'moeda', valor: (n) => n.valorCompra },
  { titulo: '% Deságio compra', formato: 'percentual', valor: (n) => n.desagioVendaPercentual },
  { titulo: 'Comprador paga', formato: 'moeda', valor: (n) => n.valorVenda },
  { titulo: 'Spread', formato: 'moeda', valor: (n) => n.spread },
  {
    titulo: 'Pagamento',
    largura: 24,
    valor: (n) =>
      [n.parcelas === 1 ? 'À vista' : `${n.parcelas}x`, n.regraPagamentoDescricao]
        .filter(Boolean)
        .join(' — '),
  },
  { titulo: 'Contrato assinado em', formato: 'data', valor: (n) => diaLocal(n.contratoAssinadoEm) },
  { titulo: 'Protocolado em', formato: 'data', valor: (n) => diaLocal(n.protocoladoEm) },
  { titulo: 'Transferência', formato: 'data', valor: (n) => diaLocal(n.dataTransferencia) },
  { titulo: 'Criada em', formato: 'data', valor: (n) => diaLocal(n.criadaEm) },
];

/** O que o assessor simulou, como ponto de partida da negociação. */
function inicioDoPedido(p: PedidoDeFechamento): InicioDaNegociacao {
  const entradas = (p.simulacao?.['entradas'] ?? {}) as Record<string, unknown>;
  const numero = (v: unknown): number | undefined =>
    typeof v === 'number' && Number.isFinite(v) ? v : undefined;
  const valorFace = numero(entradas['valorFace']);
  const desagioCompraPercentual = numero(entradas['desagioCompraPercentual']);
  const desagioVendaPercentual = numero(entradas['desagioVendaPercentual']);
  const parcelas = numero(entradas['parcelas']);
  return {
    compradorId: p.clienteId,
    parceiroId: p.assessorId,
    ...(valorFace === undefined ? {} : { valorFace }),
    ...(desagioCompraPercentual === undefined ? {} : { desagioCompraPercentual }),
    ...(desagioVendaPercentual === undefined ? {} : { desagioVendaPercentual }),
    ...(parcelas === undefined ? {} : { parcelas }),
  };
}

export function NegociacoesPontual(): JSX.Element {
  const queryClient = useQueryClient();
  const { confirmar } = useDialogo();
  const [criando, setCriando] = useState(false);
  const [abertaId, setAbertaId] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const [filtro, setFiltro] = useState<EscolhasDoPontual>(FILTRO_DO_PONTUAL_VAZIO);
  const [vendoAReceber, setVendoAReceber] = useState(false);
  /** O pedido do assessor que está virando negociação, se for o caso. */
  const [pedidoEmMontagem, setPedidoEmMontagem] = useState<PedidoDeFechamento | null>(null);
  // Memorizado: o modal reaplica o início quando ele muda, e um objeto novo a
  // cada render apagaria o que a operação digitou por cima.
  const inicio = useMemo(
    () => (pedidoEmMontagem === null ? null : inicioDoPedido(pedidoEmMontagem)),
    [pedidoEmMontagem],
  );

  /** Gravar a negociação de um pedido é atendê-lo: o assessor passa a vê-la na lista dele. */
  const atenderAoMontar = useMutation({
    mutationFn: atenderPedido,
    onSettled: () => invalidarPedidos(queryClient),
  });

  const negociacoes = useQuery({
    queryKey: ['pontual-negociacoes'],
    queryFn: () => requisicaoAutenticada<Negociacao[]>('/pontual/negociacoes'),
  });

  const invalidar = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['pontual-negociacoes'] });
  };

  /**
   * RN-703 — a exclusão some com a linha da tela, não do banco.
   *
   * Quem pode ser excluída é decisão do servidor (`podeExcluir`): hoje, em
   * fase de testes, todas; com a regra em vigor, só as canceladas.
   */
  const excluir = useMutation({
    mutationFn: (id: string) =>
      requisicaoAutenticada<void>(`/pontual/negociacoes/${id}`, { metodo: 'DELETE' }),
    onSuccess: invalidar,
  });
  // A mesma exclusão, para as negociações marcadas — só as que o servidor deixa.
  const excluirMarcadas = useAcaoEmLote<Negociacao>({
    acao: (n) => requisicaoAutenticada<void>(`/pontual/negociacoes/${n.id}`, { metodo: 'DELETE' }),
    nome: nomeDaNegociacao,
    aoTerminar: invalidar,
  });

  const lista = negociacoes.data ?? [];
  const emAberto = lista.filter(
    (n) => n.status === 'FECHADA' || n.status === 'PROTOCOLADA' || n.status === 'TRANSFERIDA',
  );
  const aReceber = lista
    .flatMap((n) => n.parcelasPagamento)
    .filter((p) => p.status === 'PENDENTE' || p.status === 'ATRASADA');
  const emAtraso = aReceber.filter((p) => p.status === 'ATRASADA').length;

  // A busca e o filtro valem para a tabela e a planilha; os cartões de cima
  // são da operação inteira. As contagens do filtro são as da busca.
  const daBusca = lista.filter((n) => naBusca(n, busca));
  const filtradas = negociacoesNoFiltro(lista, filtro, busca);
  const filtrando = busca.trim() !== '' || filtro.etapas.length > 0 || filtro.tipos.length > 0;

  const colunas: readonly ColunaTabela<Negociacao>[] = [
    {
      chave: 'partes',
      titulo: 'Descrição',
      larguraMinima: '17rem',
      celula: (n) => (
        <span className="flex flex-col">
          <button
            type="button"
            onClick={() => setAbertaId(n.id)}
            className="text-left text-sm text-conteudo-primario underline-offset-4 hover:text-acento-ciano hover:underline"
          >
            {n.fornecedorNome ?? 'Fornecedor'} → {n.compradorNome ?? 'Comprador'}
          </button>
          <span className="text-xs text-conteudo-sutil">
            {ROTULO_DO_TIPO[n.tipoCredito] ?? n.tipoCredito}
            {n.parceiroNome !== null && ` · assessor ${n.parceiroNome}`}
          </span>
        </span>
      ),
    },
    {
      chave: 'status',
      titulo: 'Situação',
      larguraMinima: '14rem',
      celula: (n) => (
        <span className="flex flex-col gap-1">
          <Selo tom={TOM_DO_STATUS[n.status] ?? 'neutro'}>
            {ROTULO_DO_STATUS[n.status] ?? n.status}
          </Selo>
          {n.protocoloTransferencia !== null && (
            <span className="valor-fiscal text-xs text-conteudo-sutil">
              protocolo {n.protocoloTransferencia}
            </span>
          )}
          {/*
            A próxima ação vem pronta do servidor (RN-704): é a mesma frase do
            detalhe, e quem responde "de quem é a bola" é o domínio.
          */}
          {n.proximaAcao.responsavel !== null && (
            <span
              className={
                n.proximaAcao.tom === 'atencao'
                  ? 'text-xs text-atencao'
                  : 'text-xs text-conteudo-secundario'
              }
            >
              {n.proximaAcao.resumo} · {QUEM[n.proximaAcao.responsavel] ?? ''}
            </span>
          )}
        </span>
      ),
    },
    {
      chave: 'valorFace',
      titulo: '$ Negociação',
      numerica: true,
      larguraMinima: '8rem',
      celula: (n) => <ValorFiscal valor={n.valorFace} tamanho="pequeno" />,
    },
    // As duas pontas pelo lado de cada parte (14/10/2026): o fornecedor (cedente)
    // vende o crédito, o comprador compra. Embaixo do valor, o deságio de cada
    // um — o percentual e quanto ele representa da face.
    {
      chave: 'valorCompra',
      titulo: '% Deságio Venda',
      numerica: true,
      larguraMinima: '11rem',
      celula: (n) => (
        <ValorComDesagio
          valor={n.valorCompra}
          face={n.valorFace}
          percentual={n.desagioCompraPercentual}
        />
      ),
    },
    {
      chave: 'valorVenda',
      titulo: '% Deságio Compra',
      numerica: true,
      larguraMinima: '11rem',
      celula: (n) => (
        <ValorComDesagio
          valor={n.valorVenda}
          face={n.valorFace}
          percentual={n.desagioVendaPercentual}
        />
      ),
    },
    {
      chave: 'spread',
      titulo: 'Spread',
      numerica: true,
      larguraMinima: '8rem',
      celula: (n) => (
        <span className="flex flex-col items-end">
          <ValorFiscal valor={n.spread} variante="economia" tamanho="pequeno" />
          {/* Sobre a negociação inteira (a face), como no cálculo do domínio. */}
          <ValorPercentual
            percentual={
              n.valorFace === 0 ? null : Math.round((n.spread / n.valorFace) * 10000) / 100
            }
            tamanho="pequeno"
            className="text-xs"
          />
        </span>
      ),
    },
    {
      chave: 'pagamento',
      titulo: 'Pagamento',
      larguraMinima: '8rem',
      // Ela e as ações ficam do tamanho do conteúdo: a lixeira perto do
      // pagamento, e a sobra da largura para as colunas de valores.
      encolher: true,
      celula: (n) => (
        <span className="flex flex-col">
          <span className="text-sm text-conteudo-secundario">
            {n.parcelas === 1 ? 'À vista' : `${n.parcelas}×`}
          </span>
          {n.regraPagamentoDescricao !== null && (
            <span className="text-xs text-conteudo-sutil">{n.regraPagamentoDescricao}</span>
          )}
        </span>
      ),
    },
    {
      chave: 'acoes',
      titulo: 'Ações',
      tituloOculto: true,
      alinhamento: 'direita',
      encolher: true,
      celula: (n) =>
        n.podeExcluir ? (
          <CelulaDeAcoes>
            <button
              type="button"
              aria-label={`Excluir a negociação ${n.fornecedorNome ?? ''} para ${n.compradorNome ?? ''}`}
              title="Excluir negociação"
              className="rounded-card p-1 text-conteudo-sutil transition-colors hover:bg-erro/10 hover:text-erro focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-erro"
              onClick={() => {
                void (async () => {
                  const ok = await confirmar({
                    titulo: 'Excluir esta negociação?',
                    descricao:
                      'Ela sai da lista. O registro continua no banco para auditoria, mas ninguém mais a vê no sistema.',
                    rotuloConfirmar: 'Excluir',
                    tom: 'perigo',
                  });
                  if (ok) excluir.mutate(n.id);
                })();
              }}
            >
              <IconeLixeira className="h-4 w-4" />
            </button>
          </CelulaDeAcoes>
        ) : (
          <span className="sr-only">Negociação em andamento: cancele antes de excluir</span>
        ),
    },
  ];

  return (
    <Pagina
      titulo="Créditos ICMS Pontual"
      descricao="Compra e venda de crédito entre fornecedor e comprador, com um assessor no meio. Quatro passos: negociar e fechar o contrato, protocolar o pedido na Receita, anexar o certificado deferido e receber."
      contagem={filtradas.length}
      acao={
        <div className="flex flex-wrap items-center gap-3">
          <BotaoExtrairXlsx
            titulo="Negociações - ICMS Pontual"
            disabled={negociacoes.data === undefined}
            colunas={COLUNAS_DA_PLANILHA}
            linhas={filtradas}
          />
          <Botao onClick={() => setCriando(true)}>Nova negociação</Botao>
        </div>
      }
    >
      {negociacoes.isError && (
        <ErroDaApi titulo="Não foi possível carregar" erro={negociacoes.error} />
      )}
      {excluir.isError && <ErroDaApi titulo="Não foi possível excluir" erro={excluir.error} />}
      <AvisoDoLote
        desfecho={excluirMarcadas.desfecho}
        feitos={(n) => (n === 1 ? '1 negociação excluída' : `${n} negociações excluídas`)}
        aoFechar={excluirMarcadas.limparDesfecho}
      />

      <div className="grid gap-4 sm:grid-cols-3">
        <CartaoIndicador
          rotulo="Em andamento"
          valor={<ValorFiscal valor={emAberto.length} moeda={false} casas={0} tamanho="grande" />}
          detalhe="Fechadas ou transferidas, ainda sem pagamento completo"
        />
        <CartaoIndicador
          rotulo="Crédito negociado"
          valor={
            <ValorFiscal
              valor={lista
                .filter((n) => n.status !== 'CANCELADA')
                .reduce((total, n) => total + n.valorFace, 0)}
              tamanho="grande"
            />
          }
          detalhe="Valor de face de todas as negociações vivas"
        />
        <CartaoIndicador
          rotulo="A receber"
          valor={
            <ValorFiscal
              valor={aReceber.reduce((total, p) => total + p.valor, 0)}
              variante="economia"
              tamanho="grande"
            />
          }
          detalhe={`${aReceber.length} parcela(s) em aberto${
            emAtraso > 0 ? `, ${emAtraso} em atraso` : ''
          } — comprador e honorários`}
          aoClicar={() => setVendoAReceber(true)}
          acao="Ver quem paga e as parcelas"
        />
      </div>

      <PedidosDaOperacao
        servico="PONTUAL"
        rotuloAbrir={(p) =>
          p.negociacaoPontualId === null ? 'Montar negociação' : 'Abrir negociação'
        }
        aoAbrir={(p) => {
          if (p.negociacaoPontualId !== null) {
            setAbertaId(p.negociacaoPontualId);
            return;
          }
          setPedidoEmMontagem(p);
          setCriando(true);
        }}
      />
      {atenderAoMontar.isError && (
        <ErroDaApi
          titulo="A negociação foi gravada, mas o pedido continua aberto — marque como atendido"
          erro={atenderAoMontar.error}
        />
      )}

      {!negociacoes.isPending && lista.length === 0 ? (
        <EstadoVazio
          titulo="Nenhuma negociação de Pontual"
          descricao="Comece pela simulação: valor da negociação, deságio de venda (do fornecedor) e de compra (do comprador). O spread que sobra é o honorário do assessor."
          acao={<Botao onClick={() => setCriando(true)}>Nova negociação</Botao>}
        />
      ) : (
        <>
          <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-end">
            <Campo
              rotulo="Buscar"
              type="search"
              placeholder="Fornecedor, comprador, assessor ou protocolo"
              className="lg:w-80"
              value={busca}
              onChange={(ev) => setBusca(ev.target.value)}
            />
            <FiltroDoPontual
              escolhas={filtro}
              aoMudar={setFiltro}
              base={daBusca}
              className="lg:w-[28rem]"
            />
          </div>
          <Cartao como="section" corpoSemPreenchimento>
            <Tabela
              legenda="Negociações de ICMS Pontual"
              densidade="compacta"
              colunas={colunas}
              dados={filtradas}
              chaveDaLinha={(n) => n.id}
              chaveDeCabecalhoDaLinha="partes"
              carregando={negociacoes.isPending}
              vazio={
                <EstadoVazio
                  compacto
                  comMoldura={false}
                  titulo="Nenhuma negociação com estes filtros"
                  descricao="Limpe a busca ou o filtro para ver todas."
                  {...(filtrando
                    ? {
                        acao: (
                          <Botao
                            variante="secundario"
                            tamanho="pequeno"
                            onClick={() => {
                              setBusca('');
                              setFiltro(FILTRO_DO_PONTUAL_VAZIO);
                            }}
                          >
                            Limpar busca e filtro
                          </Botao>
                        ),
                      }
                    : {})}
                />
              }
              selecao={{
                substantivo: { singular: 'negociação', plural: 'negociações', feminino: true },
                nomeDaLinha: nomeDaNegociacao,
                // Quem pode ser excluída é o servidor que diz (RN-703), como na lixeira.
                bloqueio: (n) =>
                  n.podeExcluir ? null : 'Negociação em andamento: cancele antes de excluir.',
                acoes: (marcadas, limpar) => (
                  <BotaoDoLote
                    rotulo="Excluir"
                    perigo
                    marcadas={marcadas}
                    limpar={limpar}
                    lote={excluirMarcadas}
                    confirmacao={(m) => ({
                      titulo:
                        m.length === 1
                          ? 'Excluir esta negociação?'
                          : `Excluir ${m.length} negociações?`,
                      descricao: `${resumirNomes(m.map(nomeDaNegociacao))}.\n\nSaem da lista. O registro continua no banco para auditoria, mas ninguém mais as vê no sistema.`,
                      rotuloConfirmar: 'Excluir',
                      tom: 'perigo',
                    })}
                  />
                ),
              }}
            />
          </Cartao>
        </>
      )}

      <ParcelasAReceber
        aberto={vendoAReceber}
        negociacoes={lista}
        aoFechar={() => setVendoAReceber(false)}
        aoAbrirNegociacao={(id) => {
          setVendoAReceber(false);
          setAbertaId(id);
        }}
      />

      <Aviso tom="informacao" titulo="Como o pagamento é combinado">
        Cada comprador paga de um jeito — "em até 5 dias após a transferência", "a partir do dia 25
        do mês subsequente". Escreva o combinado no fechamento: o sistema lê a frase, mostra o que
        entendeu e programa os vencimentos a partir do <strong>deferimento</strong> da Receita — o
        dia do certificado, não o do protocolo.
      </Aviso>

      {/* A chave muda ao abrir e ao fechar: cada "Nova negociação" começa em
          branco, sem o fornecedor, a face e os deságios da anterior. */}
      <ModalDeNegociacao
        key={criando ? 'aberto' : 'fechado'}
        aberto={criando}
        inicio={inicio}
        aoFechar={() => {
          setCriando(false);
          setPedidoEmMontagem(null);
        }}
        aoCriar={(negociacao) => {
          invalidar();
          if (pedidoEmMontagem !== null) atenderAoMontar.mutate(pedidoEmMontagem.id);
          setCriando(false);
          setPedidoEmMontagem(null);
          setAbertaId(negociacao.id);
        }}
      />

      {/* A chave é a negociação: abrir outra monta o detalhe do zero, sem
          levar avisos ("Termo anexado"), textos e checks da anterior. */}
      <DetalheDaNegociacao
        key={abertaId ?? 'nenhuma'}
        negociacaoId={abertaId}
        aoFechar={() => setAbertaId(null)}
        aoMudar={invalidar}
      />
    </Pagina>
  );
}

/**
 * O deságio de uma ponta e, embaixo, o valor dela: o percentual e quanto ele
 * representa da face — "Deságio 55% (R$ 324.500,00)" numa face de R$ 590.000 —
 * e o valor, "R$ 265.500,00". O deságio em cima foi pedido da operação (14/10).
 * O valor do deságio é a face menos o valor da ponta, os dois já arredondados
 * como estão no banco: a linha fecha com o que a tabela mostra.
 */
function ValorComDesagio(props: {
  readonly valor: number;
  readonly face: number;
  readonly percentual: number;
}): JSX.Element {
  const percentual = props.percentual.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
  return (
    <span className="flex flex-col items-end">
      <span className="whitespace-nowrap text-xs text-conteudo-sutil">
        Deságio {percentual}% ({formatarMoeda(props.face - props.valor)})
      </span>
      <ValorFiscal valor={props.valor} tamanho="pequeno" />
    </span>
  );
}

/** A negociação pelas partes, como a lista a mostra: "Fornecedor → Comprador". */
function nomeDaNegociacao(n: Negociacao): string {
  return `${n.fornecedorNome ?? 'Fornecedor'} → ${n.compradorNome ?? 'Comprador'}`;
}
